import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// ── Types (mirrors core/src/domains/ads/research/schemas.ts) ────────────────

export interface AdsResearchProductInput {
  productId?: string;
  productTitle: string;
  productDescription: string;
  productCategory?: string;
  usps?: string[];
  imageUrls?: string[];
  /** Product page / website. Forwarded to the targeted pipeline (Phase 2+)
   *  for brand-DNA + review-mining scope. */
  productUrl?: string;
  /** User-supplied competitor brand names. Forwarded as authoritative
   *  competitors — the targeted pipeline replaces LLM-derived competitor
   *  lists with these when present. */
  competitors?: string[];
}

export interface UserProblem {
  problem: string;
  evidence: string;
  severity: "high" | "medium" | "low";
  frequency: "very_common" | "common" | "occasional";
}

export interface ProductSolution {
  problem: string;
  solution: string;
  hook: string;
}

export interface AudienceInsights {
  demographics: string;
  psychographics: string;
  buyingTriggers: string[];
  commonObjections: string[];
}

export interface CompetitorWeakness {
  competitor: string;
  weakness: string;
}

export interface AdsDistilledResearch {
  userProblems: UserProblem[];
  productSolutions: ProductSolution[];
  audienceInsights: AudienceInsights;
  competitorWeaknesses: CompetitorWeakness[];
}

export interface SearchAdVariant {
  angle: string;
  headline: string;
  subheadline: string;
  cta: string;
  bodyText: string;
  targetProblem: string;
}

export interface ImageOverlayCopy {
  angle: string;
  text: string;
  targetProblem: string;
}

// 12-type UGC hook taxonomy — must stay in sync with VIDEO_SCRIPT_HOOK_TYPES in
// core/src/domains/ads/research/schemas.ts.
export type VideoScriptHookType =
  | "discovery"
  | "problem-solution"
  | "aesthetic-lifestyle"
  | "grwm"
  | "honest-review"
  | "humor"
  | "identity"
  | "beauty-closeup"
  | "pov"
  | "dependency"
  | "shock"
  | "before-after";

export type VideoScriptBeatType =
  | "HOOK"
  | "SETUP"
  | "REVEAL"
  | "PAYOFF"
  | "BRIDGE"
  | "HOOK_B"
  | "SETUP_B"
  | "REVEAL_B"
  | "PAYOFF_B";

export interface VideoScriptBeat {
  beatType: VideoScriptBeatType;
  timecode: string;
  spokenLine: string;
  visualDescription: string;
  background: string;
  creatorAction: string;
  cameraDirection: string;
  productPresence: "none" | "entering" | "hero" | "background";
  /** Optional B-roll shot direction — only populated when the user asks the
   *  agent to layer in B-roll cues via regenerate_ad_copy. */
  bRoll?: string;
  /** Optional on-screen caption text for this beat — only populated when the
   *  user asks for captions / on-screen text via regenerate_ad_copy. */
  captionOverlay?: string;
  /** Bold on-screen text overlay for this beat — always written by the
   *  distiller for new generations. Optional in the TS type for backwards
   *  compatibility with older saved artifacts that pre-date this field. */
  overlayHook?: string;
}

export interface VideoScriptPairedGroup {
  groupId: string;
  segmentIndex: 0 | 1;
  totalSegments: 2;
  /** videoIndex of the preceding script whose final frame should anchor this
   *  script's HOOK at render time. Only set when segmentIndex > 0. */
  transitionFromVideoIndex?: number;
  /** Segment 1 continuity anchor copied from segment 0 PAYOFF. */
  carryoverFromPayoff?: {
    wardrobeMatch: string;
    environmentMatch: string;
    poseAnchor: string;
  };
}

export interface VideoScript {
  angle: string;
  hookType: VideoScriptHookType;
  targetProblem: string;
  estimatedDuration: "10s" | "15s" | "30s";
  /** Persona id assigned by the research pipeline (from ugcBrief.avatars).
   *  Populated after Phase 5 completes; may be absent on older saved results. */
  assignedAvatarId?: string;
  /** Legacy marker for older saved paired 15s clone artifacts. New adclone
   *  runs use one 8-beat 30s script instead. */
  pairedGroup?: VideoScriptPairedGroup;
  beats: VideoScriptBeat[];
  /** Optional overall caption treatment description (font, colour, placement,
   *  animation) — paired with per-beat captionOverlay strings. */
  captionStyle?: string;
  /** Script-level opener overlay — bold attention-grabbing text shown over the
   *  HOOK beat. Always written by the distiller for new generations. Optional
   *  in the TS type for backwards compatibility with older saved artifacts. */
  overlayHook?: string;
}

export interface SuggestedKeyword {
  text: string;
  matchType: "broad" | "phrase" | "exact";
  intent: "branded" | "competitor" | "generic" | "long-tail";
}

export interface AdsGeneratedCopy {
  searchAdVariants: SearchAdVariant[];
  imageOverlayCopy: ImageOverlayCopy[];
  videoScripts: VideoScript[];
  suggestedKeywords?: SuggestedKeyword[];
  negativeKeywords?: string[];
  /** @deprecated Old field name — kept for backwards compat with saved artifacts */
  imageAdVariants?: SearchAdVariant[];
}

// ── UGC Brief (Phase 5) — mirrors core/src/domains/ads/research/schemas.ts ──

// Unified hook taxonomy — identical to VideoScriptHookType so avatars and
// scripts can be paired by hookType. Kept as a distinct alias for UGC brief
// consumers to stay semantically clear.
export type UgcHookType = VideoScriptHookType;

export interface UgcHook {
  rank: number;
  type: UgcHookType;
  text: string;
  whyItWorks: string;
  targetProblem: string;
}

export interface UgcVisualStyle {
  rank: number;
  style: string;
  environment: string;
  pacing: string;
  creatorAesthetic: string;
}

export interface UgcAvatarPersona {
  id: string;
  name: string;
  hookType: UgcHookType;
  narrativeArc: string;
  tone: string;
  ageRange: string;
  aesthetic: string;
  skinTone: string;
  naturalImperfection: string;
  hair: string;
  makeup: string;
  wardrobe: string;
  background: string;
  cameraSpec: string;
  energy: string;
  targetProblem: string;
  visualStyleRank: number;
}

export interface UgcBrief {
  hooks: UgcHook[];
  visualStyles: UgcVisualStyle[];
  emotionalTriggers: string[];
  recurringLanguage: string[];
  avatars: UgcAvatarPersona[];
}

// ── UGC Element Plan (mirrors core/src/domains/ads/research/schemas.ts) ─────

export type UgcElementKind =
  | "avatar-anchor"
  | "avatar-pose"
  | "product"
  | "item"
  | "background"
  | "b-roll";

export interface UgcPlannedElement {
  elementKey: string;
  kind: UgcElementKind;
  elementName: string;
  elementDescription: string;
  prompt: string;
  aspectRatio?: string;
  referenceImageUrl?: string;
  avatarBaseImageUrl?: string;
}

/**
 * @deprecated Text-frame stills are no longer generated — caption / CTA /
 * overlay copy is composited at render time by the post-render caption +
 * overlay tools. Legacy plans persisted before that change may still carry
 * this field, so the type is kept around for read compatibility.
 */
export interface UgcPlannedTextFrame {
  prompt: string;
  overlayText: string;
  assignedBeat: VideoScriptBeatType;
  includesAvatar: boolean;
  aspectRatio?: string;
  isCta?: boolean;
}

export interface UgcBeatPlaceholders {
  beatType: VideoScriptBeatType;
  prompt: string;
  elementIndices: number[];
  imageIndices: number[];
}

// Clone-mode authoring extensions — mirrors C6 in
// docs/planning/clone-moodboard-agent-refactor.md. Set by Agent Moodboard
// when composing per-video moodboards from a source ad's extraction; absent
// in research mode where ugcElementPlanner still owns the shape.
export interface CaptionSpec {
  style: string;
  fontFamily: string;
  weight: string;
  sizeScale: string;
  color: string;
  strokeColor?: string;
  position: string;
  casing: string;
  animation: string;
}

export interface TransitionBetweenBeats {
  fromBeat: VideoScriptBeatType;
  toBeat: VideoScriptBeatType;
  style: string;
}

export interface TransitionSpec {
  intoVideo: string;
  betweenBeats: TransitionBetweenBeats[];
  outOfVideo: string;
}

// Mirrors of the overlay-kind / -position unions used in clone-extract. Kept
// as local string unions to avoid a circular import from the modal file.
export type ExtractedOverlayKindMirror =
  | "overlay-hook"
  | "caption"
  | "sticker"
  | "watermark"
  | "product-label";

export type ExtractedOverlayPositionMirror =
  | "top"
  | "upper-third"
  | "middle"
  | "bottom"
  | "lower-third"
  | "center"
  | "corner";

export interface OverlayPlanItem {
  beatType: VideoScriptBeatType;
  text: string;
  kind: ExtractedOverlayKindMirror;
  position: ExtractedOverlayPositionMirror;
  timecode: string;
  normalizedGeometry?: {
    x: number;
    y: number;
    w: number;
    h: number;
    anchor?: "tl" | "tc" | "tr" | "ml" | "mc" | "mr" | "bl" | "bc" | "br";
  };
  style?: {
    color?: string;
    backgroundColor?: string;
    fontSize?: number;
    fontWeight?: number;
    fontFamily?: string;
    textAlign?: "left" | "center" | "right";
    uppercase?: boolean;
    borderRadius?: number;
    borderWidth?: number;
    borderColor?: string;
  };
  animation?: {
    animIn?: "none" | "fade" | "pop" | "slide-up" | "slide-down" | "slide-left" | "slide-right";
    animOut?: "none" | "fade" | "pop" | "slide-up" | "slide-down" | "slide-left" | "slide-right";
    animDurMs?: number;
  };
  motionPath?: Array<{
    timeSec: number;
    x: number;
    y: number;
    scale?: number;
    opacity?: number;
  }>;
  source?: {
    sourceTrackId?: string;
    confidence?: number;
  };
}

export interface UgcVideoElementPlan {
  elements: UgcPlannedElement[];
  /**
   * @deprecated New plans never emit text frames — caption / CTA / overlay
   * copy is composited at render time. Legacy plans persisted before that
   * change may carry a non-empty list, so the field is kept (optional) for
   * read compatibility.
   */
  textFrames?: UgcPlannedTextFrame[];
  beats: UgcBeatPlaceholders[];
  /** Clone-mode only — replicates the source ad's caption treatment (post-render). */
  captionSpec?: CaptionSpec;
  /** Clone-mode only — opening/between-beat/closing transition design. */
  transitionSpec?: TransitionSpec;
  /** Clone-mode only — text/hook overlay plan grounded in the source ad's
   *  on-screen text events. Rendered by the post-render overlay tool; never
   *  baked into a Banan still. */
  overlayPlan?: OverlayPlanItem[];
  platform?: "meta" | "tiktok";
  provider?: string;
  sourceSurface?: string;
  metrics?: {
    ctrRanking?: string;
    ctr?: number;
    likes?: number;
    comments?: number;
    shares?: number;
    videoDuration?: number;
    industry?: string;
    objective?: string;
  };
}

/** Slim summary of a workspace's existing reusable element, used by the UI
 *  to tag each planned element as Cached (klingElementId present = already
 *  generated + registered with Kling) vs New (Banan must generate it). */
export interface ExistingUgcElementSummary {
  elementKey: string;
  kind: UgcElementKind;
  klingElementId?: number;
}

// ── Brand DNA (targeted pipeline) ───────────────────────────────────────────
// Mirrors core/src/domains/ads/research/targeted/schemas.ts:AdsBrandDnaSchema.

export interface AdsBrandDnaColor {
  hex: string;
  role?: "primary" | "secondary" | "accent" | "neutral" | "background";
}

export interface AdsBrandDna {
  brandName?: string;
  tone?: string;
  colorPalette?: AdsBrandDnaColor[];
  typography?: { headings?: string; body?: string };
  visualStyle?: string[];
  referenceImageUrls?: string[];
}

// ── Meta Ad Library inspiration ─────────────────────────────────────────────
// Mirrors core/src/domains/ads/research/inspirationDiscovery.ts:InspirationAd.

export type InspirationCreativeType = "video" | "image" | "carousel" | "mixed" | "unknown";

export interface InspirationAd {
  adId: string;
  pageId: string;
  pageName: string;
  startDate: string;
  endDate?: string;
  runningDays: number;
  isActive: boolean;
  creativeType: InspirationCreativeType;
  /** GCS-mirrored when persistence succeeded; otherwise the upstream Meta
   *  CDN URL (which may expire in 24–48h). */
  thumbnailUrl: string;
  thumbnailPersisted: boolean;
  upstreamSnapshotUrl: string;
  videoUrl?: string;
  /** All video URLs (HD + SD when both exist). Raw Meta CDN URLs that
   *  expire in 24–48h. Detail modal uses these for inline playback. */
  videoUrls?: string[];
  /** All image URLs from snapshot.images + carousel cards. Raw Meta CDN
   *  for non-thumbnail entries — only the first is mirrored to GCS as
   *  the thumbnail. Used by the detail modal for carousel rendering. */
  imageUrls?: string[];
  bodyText: string;
  titleText: string;
  ctaText: string;
  linkUrl: string;
  publisherPlatforms: string[];
  /** facebook.com/ads/library/?id=… */
  sourceUrl: string;
  platform?: "meta" | "tiktok";
  provider?: string;
  sourceSurface?: string;
  metrics?: {
    ctrRanking?: string;
    ctr?: number;
    likes?: number;
    comments?: number;
    shares?: number;
    videoDuration?: number;
    industry?: string;
    objective?: string;
    impressions?: number;
    reach?: number;
    spend?: number;
    clicks?: number;
    views?: number;
    reactions?: number;
    currency?: string;
    displayMetrics?: Array<{
      key: string;
      label: string;
      value: string;
      rawValue?: string | number;
    }>;
  };
}

// ── Targeted pipeline raw findings (live-run only) ──────────────────────────
// Mirrors core/src/domains/ads/research/targeted/schemas.ts. Captured from the
// SSE phase events so the Studio can render TLDR cards of the evidence base
// (xAI handles + reviews) the distiller ate. Not persisted to saved briefs —
// they reset between runs and clear on reset().

export interface AccountDiscoveryAccount {
  handle: string;
  displayName?: string;
  role: "leader" | "community" | "competitor" | "media";
  why: string;
}

export interface HandleMiningQuote {
  text: string;
  tweetUrl?: string;
}

export interface HandleMiningResult {
  handle: string;
  frustrations: string[];
  desires: string[];
  needs: string[];
  notableQuotes?: HandleMiningQuote[];
}

export interface ReviewListing {
  url: string;
  title: string;
  source: "amazon" | "shopify" | "etsy" | "walmart" | "brand-site" | "other";
}

export interface ReviewMiningResult {
  listing: ReviewListing;
  pains: string[];
  complaints: string[];
  positives: string[];
  suggestions: string[];
}

export interface SavedResearchResult {
  artifactId: string;
  productId?: string;
  productTitle: string;
  productImageUrl?: string;
  distilledResearch: AdsDistilledResearch;
  /** Optional — only present after the synthesis step has run. Brief-only
   *  artifacts (Scout cut at distillation, no synthesis yet) omit this. */
  adCopy?: AdsGeneratedCopy;
  /** Optional — only present on artifacts created after Phase 5 landed. */
  ugcBrief?: UgcBrief;
  /** Optional — only present on artifacts produced by the targeted pipeline. */
  brandDna?: AdsBrandDna;
  /** Optional — top "longest-running" competitor ads scraped from Meta. */
  inspirationAds?: InspirationAd[];
  /** Agent Scout refinement version when present on live/newer artifacts. */
  version?: number;
  createdAt: string;
}

// ── Store ───────────────────────────────────────────────────────────────────

interface AdsResearchState {
  status: "idle" | "running" | "complete" | "error";
  phase: string | null;
  progress: { completed: number; total: number; track?: string } | null;
  error: string | null;

  selectedProductId: string | null;
  activeProductId: string | null;

  queryCount: number | null;
  collectionSuccessful: number | null;
  distilledResearch: AdsDistilledResearch | null;
  adCopy: AdsGeneratedCopy | null;
  ugcBrief: UgcBrief | null;
  /** Live brand DNA payload from the targeted pipeline. Set on
   *  `brand_dna_extraction` complete and on `done` (when present).
   *  Null for legacy briefs / when extraction was skipped. */
  brandDna: AdsBrandDna | null;
  /** Live competitor inspiration ads from the Meta Ad Library scrape.
   *  Set on `meta_ads_inspiration` complete and on `done` (when present).
   *  Null while the pipeline runs / when discovery returned empty. */
  inspirationAds: InspirationAd[] | null;
  /** Independent status for the parallel Meta Ad Library inspiration job.
   *  The backend kicks this off in parallel with the sequential xAI/review
   *  chain (see `targetedPipelineService.runInspirationDiscoveryStage`), so
   *  it doesn't fit the single linear `phase` field — the loader timeline
   *  reads this to mark the "Finding similar Meta Ads" step done as soon
   *  as discovery actually finishes, instead of waiting for distillation. */
  inspirationStatus: "pending" | "running" | "complete" | "failed";
  /** Ad ids the user picked from the Meta inspiration cards. Drives the
   *  Step 3 synthesis call — the synthesis service fuses the brief with
   *  these clones into the final video scripts + UGC brief. Live-state
   *  only; cleared on a new pipeline run or on synthesis completion. */
  selectedInspirationAdIds: string[];
  /** Live targeted-pipeline raw findings — captured from SSE phase events so
   *  the Studio can render evidence-base TLDRs. Live-run only: not persisted
   *  to saved briefs, cleared on the next run. */
  xaiAccounts: AccountDiscoveryAccount[];
  xaiHandleMining: HandleMiningResult[];
  reviewListings: ReviewListing[];
  reviewMining: ReviewMiningResult[];
  /** Artifact id of the brief persisted at the end of the live pipeline run.
   *  Set on SSE `done`, cleared on a new run or reset. Lets the context
   *  snapshot surface a researchBriefId to Marcus immediately, before the
   *  `useArtifacts` hook refetches `savedResults`. */
  researchBriefId: string | null;
  /** Agent Scout's current refinement version for the live research session. */
  scoutVersion: number | null;
  /** Active Scout managed-agent session before/while an artifact id is known. */
  activeScoutSessionId: string | null;
  /** Persistent artifact → Scout session map used to resume research chats. */
  scoutSessionIdsByArtifactId: Record<string, string>;

  savedResults: SavedResearchResult[];

  /** Per-script element plans surfaced after ads_plan_ugc_elements completes.
   *  Keyed by script.angle. Transient mirror — consumers read product-scoped
   *  plans from `ugc-video-plan` artifacts via `useElementPlansForProduct`;
   *  this map is only useful inside the planning surface itself. */
  elementPlansByScriptAngle: Record<string, UgcVideoElementPlan>;

  /** Workspace's existing reusable elements — captured alongside the plans
   *  so the review modal can tag each planned element as Cached vs New. */
  existingElementsByKey: Record<string, ExistingUgcElementSummary>;

  /** True while Marcus's `ads_plan_ugc_elements` tool call is in-flight (not yet
   *  resolved). Drives the Scout orbital loader on the Research tab so the user
   *  sees planning progress in the main view, not just in the chat ToolCallCard. */
  isPlanningUgcElements: boolean;

  /** Plan-run nonce for the active element plans. Scopes downstream element-ref
   *  artifacts so stale refs from old plan runs aren't displayed or dispatched. */
  activePlanRunId: string | null;

  /** Workspace that owns the persisted research slice. Used by AdsWorkspace's
   *  mount effect to tell "user reloaded the same workspace" (keep state) from
   *  "user switched workspaces" (wipe stale brief / plans). */
  persistedWorkspaceId: string | null;

  /** When set, AdsResearchTab auto-selects this product and kicks off
   *  handleStart on the next render. Cleared after consumption. Used by the
   *  AI product-creation → auto-research choreography. */
  autoStartProductId: string | null;

  // Actions
  setSelectedProductId: (id: string | null) => void;
  setActiveProductId: (id: string | null) => void;
  setStatus: (status: AdsResearchState["status"]) => void;
  setPhase: (phase: string | null) => void;
  setProgress: (progress: AdsResearchState["progress"]) => void;
  setError: (error: string | null) => void;
  setQueryCount: (count: number | null) => void;
  setCollectionSuccessful: (count: number | null) => void;
  setDistilledResearch: (research: AdsDistilledResearch | null) => void;
  setAdCopy: (copy: AdsGeneratedCopy | null) => void;
  setUgcBrief: (brief: UgcBrief | null) => void;
  setBrandDna: (dna: AdsBrandDna | null) => void;
  setInspirationAds: (ads: InspirationAd[] | null) => void;
  setInspirationStatus: (status: AdsResearchState["inspirationStatus"]) => void;
  toggleInspirationAdSelection: (adId: string) => void;
  clearInspirationAdSelection: () => void;
  setXaiAccounts: (accounts: AccountDiscoveryAccount[]) => void;
  setXaiHandleMining: (results: HandleMiningResult[]) => void;
  setReviewListings: (listings: ReviewListing[]) => void;
  setReviewMining: (results: ReviewMiningResult[]) => void;
  setResearchBriefId: (id: string | null) => void;
  setScoutVersion: (version: number | null) => void;
  setActiveScoutSessionId: (sessionId: string | null) => void;
  setScoutSessionForArtifact: (artifactId: string, sessionId: string) => void;
  setSavedResults: (results: SavedResearchResult[]) => void;
  setElementPlans: (
    plans: Array<{ scriptAngle: string; plan: UgcVideoElementPlan }>,
  ) => void;
  setExistingElements: (elements: ExistingUgcElementSummary[]) => void;
  clearElementPlans: () => void;
  setIsPlanningUgcElements: (v: boolean) => void;
  setActivePlanRunId: (id: string | null) => void;
  /** Tag the persisted slice with the active workspace. AdsWorkspace calls this
   *  on mount and on workspace switch; the next reload uses it to decide whether
   *  the rehydrated brief/plans still belong to the current workspace. */
  setPersistedWorkspaceId: (id: string | null) => void;
  setAutoStartProductId: (id: string | null) => void;
  reset: () => void;
}

const SCOUT_SESSION_MAP_KEY = "wiser:adsResearch:scoutSessionsByArtifact";
const SCOUT_ACTIVE_SESSION_KEY = "wiser:adsResearch:activeScoutSession";

function hydrateScoutSessionMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(SCOUT_SESSION_MAP_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const entries = Object.entries(parsed).filter(
      (entry): entry is [string, string] =>
        typeof entry[0] === "string" &&
        entry[0].length > 0 &&
        typeof entry[1] === "string" &&
        entry[1].length > 0,
    );
    return Object.fromEntries(entries);
  } catch {
    return {};
  }
}

function persistScoutSessionMap(map: Record<string, string>) {
  try {
    localStorage.setItem(SCOUT_SESSION_MAP_KEY, JSON.stringify(map));
  } catch {
    // localStorage unavailable/full — session resume gracefully degrades.
  }
}

function hydrateActiveScoutSessionId(): string | null {
  try {
    const raw = sessionStorage.getItem(SCOUT_ACTIVE_SESSION_KEY);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

function persistActiveScoutSessionId(sessionId: string | null) {
  try {
    if (sessionId) sessionStorage.setItem(SCOUT_ACTIVE_SESSION_KEY, sessionId);
    else sessionStorage.removeItem(SCOUT_ACTIVE_SESSION_KEY);
  } catch {
    // sessionStorage unavailable — non-fatal.
  }
}

const INITIAL_STATE = {
  status: "idle" as const,
  phase: null,
  progress: null,
  error: null,
  selectedProductId: null,
  activeProductId: null,
  queryCount: null,
  collectionSuccessful: null,
  distilledResearch: null,
  adCopy: null,
  ugcBrief: null,
  brandDna: null,
  inspirationAds: null,
  inspirationStatus: "pending" as const,
  selectedInspirationAdIds: [],
  xaiAccounts: [],
  xaiHandleMining: [],
  reviewListings: [],
  reviewMining: [],
  researchBriefId: null,
  scoutVersion: null,
  activeScoutSessionId: hydrateActiveScoutSessionId(),
  scoutSessionIdsByArtifactId: hydrateScoutSessionMap(),
  savedResults: [],
  elementPlansByScriptAngle: {},
  existingElementsByKey: {},
  isPlanningUgcElements: false,
  activePlanRunId: null,
  persistedWorkspaceId: null,
  autoStartProductId: null,
};

// Page-reload survival: the Meta Ad Library clones and the live research
// brief slices live only in this Zustand store, so a full reload used to wipe
// them. Persist the post-run, user-facing slices to localStorage via Zustand's
// `persist` middleware (same pattern as `useOrganizationStore`). Live run-state
// (status/phase/progress/error, SSE-only findings, planning flag) is left out
// so a reload starts from an idle UI even if a previous run was mid-flight.
//
// Element-plan state (elementPlansByScriptAngle, existingElementsByKey,
// activePlanRunId) is deliberately NOT persisted — plans hydrate from the
// product-scoped `ugc-video-plan` artifacts on demand via
// `useElementPlansForProduct`. The store fields stay for live editing, but a
// reload re-reads the artifacts.
const PERSIST_KEY = "wiser:adsResearch:live";

type PersistedAdsResearchState = Pick<
  AdsResearchState,
  | "selectedProductId"
  | "activeProductId"
  | "distilledResearch"
  | "adCopy"
  | "ugcBrief"
  | "brandDna"
  | "inspirationAds"
  | "selectedInspirationAdIds"
  | "researchBriefId"
  | "scoutVersion"
  | "persistedWorkspaceId"
>;

export const useAdsResearchStore = create<AdsResearchState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      setSelectedProductId: (id) => set({ selectedProductId: id }),
      setActiveProductId: (id) => set({ activeProductId: id }),
      setStatus: (status) => set({ status }),
      setPhase: (phase) => set({ phase }),
      setProgress: (progress) => set({ progress }),
      setError: (error) => set({ error }),
      setQueryCount: (count) => set({ queryCount: count }),
      setCollectionSuccessful: (count) => set({ collectionSuccessful: count }),
      setDistilledResearch: (research) => set({ distilledResearch: research }),
      setAdCopy: (copy) => set({ adCopy: copy }),
      setUgcBrief: (brief) => set({ ugcBrief: brief }),
      setBrandDna: (dna) => set({ brandDna: dna }),
      setInspirationAds: (ads) => set({ inspirationAds: ads }),
      setInspirationStatus: (status) => set({ inspirationStatus: status }),
      toggleInspirationAdSelection: (adId) =>
        set((state) => ({
          selectedInspirationAdIds: state.selectedInspirationAdIds.includes(adId)
            ? state.selectedInspirationAdIds.filter((id) => id !== adId)
            : [...state.selectedInspirationAdIds, adId],
        })),
      clearInspirationAdSelection: () => set({ selectedInspirationAdIds: [] }),
      setXaiAccounts: (accounts) => set({ xaiAccounts: accounts }),
      setXaiHandleMining: (results) => set({ xaiHandleMining: results }),
      setReviewListings: (listings) => set({ reviewListings: listings }),
      setReviewMining: (results) => set({ reviewMining: results }),
      setResearchBriefId: (id) => set({ researchBriefId: id }),
      setScoutVersion: (version) => set({ scoutVersion: version }),
      setActiveScoutSessionId: (sessionId) => {
        persistActiveScoutSessionId(sessionId);
        set({ activeScoutSessionId: sessionId });
      },
      setScoutSessionForArtifact: (artifactId, sessionId) =>
        set((state) => {
          if (!artifactId || !sessionId) return state;
          const next = { ...state.scoutSessionIdsByArtifactId, [artifactId]: sessionId };
          persistScoutSessionMap(next);
          persistActiveScoutSessionId(sessionId);
          return {
            scoutSessionIdsByArtifactId: next,
            activeScoutSessionId: sessionId,
          };
        }),
      setSavedResults: (results) => set({ savedResults: results }),
      setElementPlans: (plans) =>
        set({
          elementPlansByScriptAngle: plans.reduce<Record<string, UgcVideoElementPlan>>((acc, { scriptAngle, plan }) => {
            acc[scriptAngle] = plan;
            return acc;
          }, {}),
          isPlanningUgcElements: false,
        }),
      setExistingElements: (elements) =>
        set({
          existingElementsByKey: elements.reduce<Record<string, ExistingUgcElementSummary>>((acc, e) => {
            acc[e.elementKey] = e;
            return acc;
          }, {}),
        }),
      clearElementPlans: () => set({ elementPlansByScriptAngle: {}, existingElementsByKey: {}, activePlanRunId: null }),
      setIsPlanningUgcElements: (v) => set({ isPlanningUgcElements: v }),
      setActivePlanRunId: (id) => set({ activePlanRunId: id }),
      setPersistedWorkspaceId: (id) => set({ persistedWorkspaceId: id }),
      setAutoStartProductId: (id) => set({ autoStartProductId: id }),
      reset: () =>
        set((state) => ({
          ...INITIAL_STATE,
          savedResults: state.savedResults,
          scoutSessionIdsByArtifactId: state.scoutSessionIdsByArtifactId,
          activeScoutSessionId: null,
          persistedWorkspaceId: state.persistedWorkspaceId,
        })),
    }),
    {
      name: PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist the post-run, user-facing slices. Status/phase/progress
      // and SSE-only findings stay in-memory so a reload after a mid-flight
      // pipeline lands on a clean idle UI rather than a frozen loader.
      partialize: (state): PersistedAdsResearchState => ({
        selectedProductId: state.selectedProductId,
        activeProductId: state.activeProductId,
        distilledResearch: state.distilledResearch,
        adCopy: state.adCopy,
        ugcBrief: state.ugcBrief,
        brandDna: state.brandDna,
        inspirationAds: state.inspirationAds,
        selectedInspirationAdIds: state.selectedInspirationAdIds,
        researchBriefId: state.researchBriefId,
        scoutVersion: state.scoutVersion,
        persistedWorkspaceId: state.persistedWorkspaceId,
      }),
    },
  ),
);
